import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Expenses() {
  const { user } = useAuth();
  const isManager = user?.role === "SALES_MANAGER" || user?.role === "ORG_ADMIN";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "TRAVEL", amount: 0, incurred_on: new Date().toISOString().slice(0, 10), description: "" });

  const load = async () => { const { data } = await api.get("/sales/expenses"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => { try { await api.post("/sales/expenses", form); toast.success("Submitted"); setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const decide = async (e, action) => { try { await api.post(`/sales/expenses/${e.id}/decide`, { action }); toast.success(`Expense ${action.toLowerCase()}d`); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-expenses">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Expense claims</h1>
          <p className="text-muted-foreground">Submit travel, fuel and allowance claims. Managers approve here.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="expense-submit-btn"><Plus className="w-4 h-4 mr-1" /> Submit claim</Button>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Submitted by</TableHead><TableHead>Kind</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="font-mono text-xs">{e.incurred_on}</TableCell>
                <TableCell className="text-sm">{e.user_email}</TableCell>
                <TableCell><Badge variant="outline">{e.kind}</Badge></TableCell>
                <TableCell>₹{(e.amount || 0).toLocaleString()}</TableCell>
                <TableCell><Badge>{e.status}</Badge></TableCell>
                <TableCell className="text-xs">{e.description || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  {isManager && e.status === "SUBMITTED" && (
                    <>
                      <Button size="sm" onClick={() => decide(e, "APPROVE")} data-testid={`expense-approve-${e.id}`}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => decide(e, "REJECT")} data-testid={`expense-reject-${e.id}`}><X className="w-4 h-4" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No claims yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="expense-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{["TRAVEL","FUEL","ALLOWANCE","OTHER"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || "0") })} data-testid="expense-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={form.incurred_on} onChange={(e) => setForm({ ...form, incurred_on: e.target.value })} /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="expense-save">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
